import apiUrl from './api';
import { postWithAuth, postStreamWithAuth } from '@/lib/httpAdapter';

const STORE_PR_REVIEW_CONFIG_URL = apiUrl + 'storePRReviewConfig/';
const FETCH_PR_REVIEW_CONFIG_URL = apiUrl + 'fetchPRReviewConfig/';
const FETCH_PR_LEARNING_URL = apiUrl + 'fetchPRLearning/';
const CONFIGURE_PR_BOT_FOR_ORG_GITLAB_URL =
  apiUrl + 'configurePRBotForOrgGitlab/';
const CONFIGURE_PR_BOT_FOR_ORG_BITBUCKET_URL =
  apiUrl + 'configurePRBotForOrgBitbucket/';
const CONFIGURE_PR_BOT_FOR_ORG_BITBUCKET_BULK_URL =
  apiUrl + 'configurePRBotForOrgBitbucketBulk/';
const FETCH_PR_BOT_REPOS_URL = apiUrl + 'fetchPRBotRepos/';
const STORE_PR_GUIDELINES_URL = apiUrl + 'storePRGuidelines/';
const FETCH_PR_GUIDELINES_URL = apiUrl + 'fetchPRGuidelines/';
const FETCH_PR_GUIDELINE_CONTENT_URL = apiUrl + 'fetchPRGuidelineContent/';
const FETCH_GUIDELINES_FOR_REPOS_URL = apiUrl + 'fetchGuidelinesForRepos/';
const CONFIGURE_GUIDELINES_FOR_REPOS_URL =
  apiUrl + 'configureGuidelinesForRepos/';
const DELETE_CODEBASE_GUIDELINES_URL = apiUrl + 'deleteCodebaseGuidelines/';
const PROCESS_HUMAN_COMMENTS_URL = apiUrl + 'processHumanComments/';
const PROCESS_HUMAN_COMMENTS_STREAM_URL =
  apiUrl + 'processHumanCommentsStream/';
const FETCH_TOTAL_PR_COUNT_BY_REPOS_URL = apiUrl + 'fetchTotalPRCountByRepos/';
const SCAN_REPO_MARKDOWN_FILES_URL = apiUrl + 'scanRepoMarkdownFiles/';
const FETCH_REPO_MARKDOWN_FILES_URL = apiUrl + 'fetchRepoMarkdownFiles/';
const UPDATE_REPO_MARKDOWN_FILE_URL = apiUrl + 'updateRepoMarkdownFile/';
const STORE_PR_REVIEW_EXCLUDE_PATTERNS_URL =
  apiUrl + 'storePRReviewExcludePatterns/';
const FETCH_PR_REVIEW_EXCLUDE_PATTERNS_URL =
  apiUrl + 'fetchPRReviewExcludePatterns/';
const BULK_UPDATE_REPO_MARKDOWN_FILES_URL =
  apiUrl + 'bulkUpdateRepoMarkdownFiles/';
const FETCH_REPO_MARKDOWN_FOLDER_CONFIGS_URL =
  apiUrl + 'fetchRepoMarkdownFolderConfigs/';
const UPDATE_REPO_MARKDOWN_FOLDER_CONFIG_URL =
  apiUrl + 'updateRepoMarkdownFolderConfig/';

interface PRReviewsConfigType {
  userUUID: string;
  orgUUID?: string;
  settings: {
    priorityLevel: string;
    highLevelSummary: boolean;
    walkThrough: boolean;
    changedFilesSummary: boolean;
    autoTitle: boolean;
    autoLinkTicket: boolean;
    learningsCrossRepo: boolean;
    mode: string;
    enableLGTM: boolean;
    enableDiagram: boolean;
    static_scan_enabled: boolean;
    pr_metrics_enabled: boolean;
    pr_review_enabled: boolean;
    pr_scoring_enabled: boolean;
    review_on_every_push: boolean;
    auto_resolve_on_reply: boolean;
    comment_style: string;
    show_confidence_score: boolean;
    reply_to_other_bots_comments: boolean;
    auto_approve_on_safe: boolean;
    max_comments: number;
  };
}
export const storePRReviewsConfig = async ({
  userUUID,
  orgUUID,
  settings,
}: PRReviewsConfigType) => {
  try {
    const payload: {
      userUUID: string;
      priorityLevel: string;
      highLevelSummary: boolean;
      walkThrough: boolean;
      changedFilesSummary: boolean;
      autoTitle: boolean;
      autoLinkTicket: boolean;
      learningsCrossRepo: boolean;
      orgUUID?: string;
      mode: string;
      enableLGTM: boolean;
      enableDiagram: boolean;
      static_scan_enabled: boolean;
      pr_metrics_enabled: boolean;
      pr_review_enabled: boolean;
      pr_scoring_enabled: boolean;
      review_on_every_push: boolean;
      auto_resolve_on_reply: boolean;
      comment_style: string;
      show_confidence_score: boolean;
      reply_to_other_bots_comments: boolean;
      auto_approve_on_safe: boolean;
      max_comments: number;
    } = {
      userUUID,
      priorityLevel: settings.priorityLevel,
      highLevelSummary: settings.highLevelSummary,
      walkThrough: settings.walkThrough,
      changedFilesSummary: settings.changedFilesSummary,
      autoTitle: settings.autoTitle,
      autoLinkTicket: settings.autoLinkTicket,
      learningsCrossRepo: settings.learningsCrossRepo,
      mode: settings.mode,
      enableLGTM: settings.enableLGTM,
      enableDiagram: settings.enableDiagram,
      static_scan_enabled: settings.static_scan_enabled,
      pr_metrics_enabled: settings.pr_metrics_enabled,
      pr_review_enabled: settings.pr_review_enabled,
      pr_scoring_enabled: settings.pr_scoring_enabled,
      review_on_every_push: settings.review_on_every_push,
      auto_resolve_on_reply: settings.auto_resolve_on_reply,
      comment_style: settings.comment_style,
      show_confidence_score: settings.show_confidence_score,
      reply_to_other_bots_comments: settings.reply_to_other_bots_comments,
      auto_approve_on_safe: settings.auto_approve_on_safe,
      max_comments: settings.max_comments,
    };
    if (orgUUID) {
      payload.orgUUID = orgUUID;
    }

    const response = await postWithAuth(STORE_PR_REVIEW_CONFIG_URL, payload);

    const data = JSON.parse(await response.json());

    if (!data.error) {
      return data;
    }

    return { error: data.error };
  } catch (error) {
    return { error: error };
  }
};
export const fetchPRReviewsConfig = async (
  userUUID: string,
  orgUUID?: string
) => {
  try {
    const payload: { userUUID: string; orgUUID?: string } = {
      userUUID,
    };
    if (orgUUID) {
      payload['orgUUID'] = orgUUID;
    }

    const response = await postWithAuth(FETCH_PR_REVIEW_CONFIG_URL, payload);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const raw = await response.json();
    const data = typeof raw === 'string' ? JSON.parse(raw) : raw;

    if (!data.error) {
      return data;
    }

    return { error: data.error };
  } catch (error) {
    return { error: error };
  }
};
export const fetchPRLearnings = async (
  userUUID: string,
  orgUUID?: string,
  opts?: { limit?: number; offset?: number; search?: string }
) => {
  try {
    const payload: {
      userUUID: string;
      orgUUID?: string;
      limit?: number;
      offset?: number;
      search?: string;
    } = {
      userUUID,
    };
    if (orgUUID) {
      payload['orgUUID'] = orgUUID;
    }
    if (opts?.limit != null) {
      payload['limit'] = opts.limit;
    }
    if (opts?.offset != null) {
      payload['offset'] = opts.offset;
    }
    if (opts?.search) {
      payload['search'] = opts.search;
    }

    const response = await postWithAuth(FETCH_PR_LEARNING_URL, payload);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const raw = await response.json();
    const data = typeof raw === 'string' ? JSON.parse(raw) : raw;

    if (!data.error) {
      return data;
    }

    return { error: data.error };
  } catch (error) {
    return { error: error };
  }
};

export const configurePRBotForOrgGitlab = async ({
  UserUUID,
  RepoUrl,
  OrgUUID,
  GroupAccessToken,
}: {
  UserUUID: string;
  RepoUrl: string;
  OrgUUID?: string;
  GroupAccessToken?: string;
}) => {
  try {
    const payload: {
      UserUUID: string;
      RepoUrl: string;
      OrgUUID?: string;
      GroupAccessToken?: string;
    } = {
      UserUUID,
      RepoUrl,
    };
    if (OrgUUID) {
      payload['OrgUUID'] = OrgUUID;
    }
    if (GroupAccessToken) {
      payload['GroupAccessToken'] = GroupAccessToken;
    }

    const response = await postWithAuth(
      CONFIGURE_PR_BOT_FOR_ORG_GITLAB_URL,
      payload
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const raw = await response.json();
    const data = typeof raw === 'string' ? JSON.parse(raw) : raw;

    if (!data.Error) {
      return data;
    }
    return { error: data.Error };
  } catch (error) {
    return { error: String(error) };
  }
};

interface ConfigurePRBotResponse {
  error?: string;
  [key: string]: unknown;
}

export const configurePRBotForOrgBitbucket = async ({
  UserUUID,
  RepoUrl,
  OrgUUID,
  AccessToken,
}: {
  UserUUID: string;
  RepoUrl: string;
  OrgUUID?: string;
  AccessToken?: string;
}): Promise<ConfigurePRBotResponse> => {
  try {
    const payload: {
      UserUUID: string;
      RepoUrl: string;
      OrgUUID?: string;
      AccessToken?: string;
    } = {
      UserUUID,
      RepoUrl,
    };
    if (OrgUUID) {
      payload['OrgUUID'] = OrgUUID;
    }
    if (AccessToken) {
      payload['AccessToken'] = AccessToken;
    }

    const response = await postWithAuth(
      CONFIGURE_PR_BOT_FOR_ORG_BITBUCKET_URL,
      payload
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const raw = await response.json();
    const data = typeof raw === 'string' ? JSON.parse(raw) : raw;

    if (!data.Error) {
      return data;
    }
    return { error: data.Error };
  } catch (error) {
    return { error: String(error) };
  }
};

export interface BitbucketBulkConfigureItem {
  RepoUrl: string;
  Enable: boolean;
}

export interface BitbucketBulkConfigureResultRow {
  RepoUrl: string;
  Enable: boolean | null;
  Error: string | null;
  PRBotUUID: string;
}

/** One HTTP round-trip: same per-repo configure/toggle semantics as single-repo calls. */
export const configurePRBotForOrgBitbucketBulk = async ({
  UserUUID,
  OrgUUID,
  Items,
  AccessToken,
}: {
  UserUUID: string;
  OrgUUID: string;
  Items: BitbucketBulkConfigureItem[];
  AccessToken?: string;
}): Promise<{
  Results?: BitbucketBulkConfigureResultRow[];
  error?: string;
}> => {
  try {
    const payload: Record<string, unknown> = {
      UserUUID,
      OrgUUID,
      Items,
    };
    if (AccessToken) {
      payload.AccessToken = AccessToken;
    }

    const response = await postWithAuth(
      CONFIGURE_PR_BOT_FOR_ORG_BITBUCKET_BULK_URL,
      payload
    );

    if (!response.ok) {
      let msg = `HTTP error! status: ${response.status}`;
      try {
        const raw = await response.json();
        const data = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if (typeof data?.Error === 'string' && data.Error) {
          msg = data.Error;
        }
      } catch {
        /* non-JSON error body (e.g. gateway HTML) */
      }
      return { error: msg };
    }

    const raw = await response.json();
    const data = typeof raw === 'string' ? JSON.parse(raw) : raw;

    if (data?.Error) {
      return { error: String(data.Error) };
    }

    return { Results: Array.isArray(data?.Results) ? data.Results : [] };
  } catch (error) {
    return { error: String(error) };
  }
};

// Save Bitbucket App Password at org level
export const saveBitbucketBotToken = async ({
  OrgUUID,
  BitbucketBotToken,
  BitbucketBotEmail,
}: {
  OrgUUID: string;
  BitbucketBotToken: string;
  BitbucketBotEmail: string;
}): Promise<{ error?: string }> => {
  try {
    const response = await postWithAuth(apiUrl + 'saveBitbucketBotToken/', {
      OrgUUID,
      BitbucketBotToken,
      BitbucketBotEmail,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const raw = await response.json();
    const data = typeof raw === 'string' ? JSON.parse(raw) : raw;

    if (data.Error) return { error: data.Error };
    return {};
  } catch (error) {
    return { error: String(error) };
  }
};

// Check if org has a Bitbucket App Password configured
export const fetchBitbucketBotToken = async ({
  OrgUUID,
}: {
  OrgUUID: string;
}): Promise<{ HasToken: boolean; Email: string; error?: string }> => {
  try {
    const response = await postWithAuth(apiUrl + 'fetchBitbucketBotToken/', {
      OrgUUID,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const raw = await response.json();
    const data = typeof raw === 'string' ? JSON.parse(raw) : raw;

    if (data.Error) return { HasToken: false, Email: '', error: data.Error };
    return { HasToken: data.HasToken ?? false, Email: data.Email ?? '' };
  } catch (error) {
    return { HasToken: false, Email: '', error: String(error) };
  }
};

export interface TeamObject {
  id: number;
  uuid: string;
  team_name: string;
}

export interface RepoObject {
  repo_name: string;
  team?: TeamObject;
  [key: string]: any;
}

export interface PRBotResponse {
  InstalledRepos: RepoObject[];
  Error: string | null;
  TotalCount?: number;
  HasMore?: boolean;
}

export interface FetchPRBotReposParams {
  UserUUID: string;
  OrgUUID?: string;
  Page?: number;
  Skip?: number;
  Limit?: number;
}

export const fetchPRBotRepos = async (
  params: FetchPRBotReposParams
): Promise<PRBotResponse> => {
  try {
    const payload: FetchPRBotReposParams = {
      UserUUID: params.UserUUID,
    };
    if (params.OrgUUID) {
      payload['OrgUUID'] = params.OrgUUID;
    }
    if (params.Page !== undefined) {
      payload['Page'] = params.Page;
    }
    if (params.Skip !== undefined) {
      payload['Skip'] = params.Skip;
    }
    if (params.Limit !== undefined) {
      payload['Limit'] = params.Limit;
    }

    const response = await postWithAuth(FETCH_PR_BOT_REPOS_URL, payload);

    if (!response.ok) {
      throw new Error(
        `HTTP error! status: ${response.status} ${response.statusText}`
      );
    }

    const raw = await response.json();
    const data = typeof raw === 'string' ? JSON.parse(raw) : raw;

    return {
      InstalledRepos: Array.isArray(data?.InstalledRepos)
        ? data.InstalledRepos
        : [],
      Error: data?.Error ?? null,
      TotalCount: data?.TotalCount,
      HasMore: data?.HasMore ?? false,
    };
  } catch (error) {
    console.error('fetchPRBotRepos caught error:', {
      error,
      message: error instanceof Error ? error.message : String(error),
      timestamp: new Date().toISOString(),
    });
    return { InstalledRepos: [], Error: String(error), HasMore: false };
  }
};

export interface GuidelineDocument {
  file_name: string;
  file_content?: string;
  codebases?: string[];
  doc_url?: string;
  guideline_uuid?: string;
  created_at?: string;
  updated_at?: string;
}

export interface StoreGuidelinesResponse {
  guideline_uuid?: string;
  error?: string | null;
  message?: string;
  guidelines?: GuidelineDocument[];
}

export interface GuidelinesResponse {
  guidelines?: GuidelineDocument[];
  total_count?: number;
  has_more?: boolean;
  error?: string | null;
}

export interface FetchPRGuidelinesOpts {
  limit?: number;
  offset?: number;
  search?: string;
}

export const storePRGuidelines = async (
  userUUID: string,
  documents: GuidelineDocument[],
  orgUUID: string
): Promise<StoreGuidelinesResponse> => {
  try {
    const doc = documents[0];
    const payload: any = {
      userUUID,
      org_uuid: orgUUID,
      file_name: doc.file_name,
      file_content: doc.file_content,
      codebases: doc.codebases || [],
    };

    // Include guideline_uuid if it exists (for updates)
    if (doc.guideline_uuid) {
      payload.guideline_uuid = doc.guideline_uuid;
    }

    const response = await postWithAuth(STORE_PR_GUIDELINES_URL, payload);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    // Consider using a debug flag to conditionally log in non-production environments

    if (data.guideline_uuid) {
      return {
        guidelines: [
          {
            ...doc,
            guideline_uuid: data.guideline_uuid,
          },
        ],
        guideline_uuid: data.guideline_uuid,
        message: data.message,
        error: null,
      };
    }

    return { error: data.error || 'No guideline UUID returned' };
  } catch (error) {
    return { error: String(error) };
  }
};

export const fetchPRGuidelines = async (
  userUUID: string,
  orgUUID: string,
  opts?: FetchPRGuidelinesOpts
): Promise<GuidelinesResponse> => {
  try {
    const payload: {
      userUUID: string;
      org_uuid: string;
      limit?: number;
      offset?: number;
      search?: string;
    } = {
      userUUID,
      org_uuid: orgUUID,
    };
    if (opts?.limit !== undefined) payload.limit = opts.limit;
    if (opts?.offset !== undefined) payload.offset = opts.offset;
    if (opts?.search) payload.search = opts.search;

    const response = await postWithAuth(FETCH_PR_GUIDELINES_URL, payload);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (!data.error) {
      return {
        guidelines: data.guidelines,
        total_count: data.total_count,
        has_more: data.has_more,
        error: null,
      };
    }

    return { error: data.error };
  } catch (error) {
    return { error: String(error) };
  }
};

export interface GuidelineContentResponse {
  file_content?: string;
  error?: string;
}

export const fetchPRGuidelineContent = async (
  guideline_uuid: string,
  org_uuid: string
): Promise<GuidelineContentResponse> => {
  try {
    const response = await postWithAuth(FETCH_PR_GUIDELINE_CONTENT_URL, {
      guideline_uuid,
      org_uuid,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (!data.error) {
      return { file_content: data.file_content };
    }

    return { error: data.error };
  } catch (error) {
    return { error: String(error) };
  }
};

export interface ReposForGuidelineResponse {
  repos?: string[];
  error?: string | null;
}

const fetchGuidelinesForRepos = async (
  orgUUID: string,
  guideline_uuid: string,
  file_name?: string
): Promise<ReposForGuidelineResponse> => {
  try {
    const payload = {
      org_uuid: orgUUID,
      guideline_uuid: guideline_uuid,
      file_name: file_name,
    };

    const response = await postWithAuth(
      FETCH_GUIDELINES_FOR_REPOS_URL,
      payload
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (!data.error) {
      // Handle both possible response formats
      const repoList = data.repo_names || data.repos || [];
      return { repos: repoList, error: null };
    }

    return { error: data.error };
  } catch (error) {
    console.error('Error in fetchGuidelinesForRepos:', error);
    return { error: String(error) };
  }
};

export interface ConfigureGuidelinesResponse {
  success?: boolean;
  error?: string | null;
}

export const configureGuidelinesForRepos = async (
  orgUUID: string,
  guideline_uuid: string,
  repoNames: string[],
  file_name?: string
): Promise<ConfigureGuidelinesResponse> => {
  try {
    const payload = {
      org_uuid: orgUUID,
      guideline_uuid: guideline_uuid,
      repo_names: repoNames,
      file_name: file_name,
    };

    const response = await postWithAuth(
      CONFIGURE_GUIDELINES_FOR_REPOS_URL,
      payload
    );

    const data = await response.json();

    if (!data.error) {
      return { success: true, error: null };
    }

    return { success: false, error: data.error };
  } catch (error) {
    console.error('Error in configureGuidelinesForRepos:', error);
    return { success: false, error: String(error) };
  }
};

export interface DeleteGuidelinesResponse {
  success?: boolean;
  error?: string | null;
}

export const deleteCodebaseGuidelines = async (
  orgUUID: string,
  fileName: string
): Promise<DeleteGuidelinesResponse> => {
  try {
    const payload = {
      org_uuid: orgUUID,
      file_name: fileName,
    };

    const response = await postWithAuth(
      DELETE_CODEBASE_GUIDELINES_URL,
      payload
    );

    const data = await response.json();

    if (!data.error) {
      return { success: true, error: null };
    }

    return { success: false, error: data.error };
  } catch (error) {
    return { success: false, error: String(error) };
  }
};

const processHumanComments = async (
  repoName: string,
  orgUUID: string,
  currentDate: string,
  accessToken?: string,
  prNumCap: number = 100,
  userUUID?: string,
  codebases?: string[]
): Promise<{
  comment_pairs?: any[];
  insights?: any[];
  guideline_uuid?: string;
  error?: string;
}> => {
  try {
    const payload: any = {
      repo_name: repoName,
      access_token: accessToken || '', // Use provided access token or empty string
      current_date: currentDate,
      pr_num_cap: prNumCap,
      org_uuid: orgUUID,
    };

    // Only include user_uuid if provided
    if (userUUID) {
      payload.user_uuid = userUUID;
    }

    // Include codebases if provided
    if (codebases && codebases.length > 0) {
      payload.codebases = codebases;
    }

    const response = await fetch(PROCESS_HUMAN_COMMENTS_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = JSON.parse(await response.json());

    if (data.error) {
      return {
        comment_pairs: [],
        insights: [],
        guideline_uuid: undefined,
        error: data.error,
      };
    }

    return {
      comment_pairs: data.comment_pairs || [],
      insights: data.insights || [],
      guideline_uuid: data.guideline_uuid,
      error: undefined,
    };
  } catch (error) {
    return {
      comment_pairs: [],
      insights: [],
      guideline_uuid: undefined,
      error: String(error),
    };
  }
};

export interface ProcessHumanCommentsProgress {
  status: 'started' | 'progress' | 'completed' | 'error';
  message: string;
  progress: number;
  guideline_uuid?: string;
  insights?: any[];
}

export const processHumanCommentsStream = async (
  repoName: string,
  orgUUID: string,
  currentDate: string,
  onProgress: (progress: ProcessHumanCommentsProgress) => void,
  accessToken?: string,
  prNumCap: number = 100,
  userUUID?: string,
  codebases?: string[]
): Promise<{
  comment_pairs?: any[];
  insights?: any[];
  guideline_uuid?: string;
  error?: string;
  errorKind?: 'empty' | 'failure';
}> => {
  try {
    const FRIENDLY_ERR =
      'Unable to generate guidelines for this repo at this time.';
    // Backend (pr_reviewer/handler.py) returns these as status:"error", but they
    // are benign "nothing to learn" outcomes — not system failures. Surface them
    // softly (see the Guidelines component's toast handling).
    const BENIGN_ERRORS = [
      'No human review comments found for this repository.',
      'Could not derive any guidelines from the review comments.',
    ];
    const payload: any = {
      repo_name: repoName,
      access_token: accessToken || '',
      current_date: currentDate,
      pr_num_cap: prNumCap,
      org_uuid: orgUUID,
    };

    if (userUUID) {
      payload.user_uuid = userUUID;
    }

    if (codebases && codebases.length > 0) {
      payload.codebases = codebases;
    }

    const response = await postStreamWithAuth(
      PROCESS_HUMAN_COMMENTS_STREAM_URL,
      payload
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    if (!response.body) {
      throw new Error('Response body is null');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let finalResult: any = null;

    while (true) {
      const { done, value } = await reader.read();

      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = JSON.parse(line.slice(6));
          onProgress(data);

          if (data.status === 'completed') {
            finalResult = {
              comment_pairs: [],
              insights: data.insights || [],
              guideline_uuid: data.guideline_uuid,
              error: undefined,
            };
          } else if (data.status === 'error') {
            const message = data.message || FRIENDLY_ERR;
            return {
              comment_pairs: [],
              insights: [],
              guideline_uuid: undefined,
              error: message,
              errorKind: BENIGN_ERRORS.includes(message) ? 'empty' : 'failure',
            };
          }
        }
      }
    }

    return (
      finalResult || {
        comment_pairs: [],
        insights: [],
        guideline_uuid: undefined,
        error: FRIENDLY_ERR,
        errorKind: 'failure',
      }
    );
  } catch (error) {
    return {
      comment_pairs: [],
      insights: [],
      guideline_uuid: undefined,
      error: 'Unable to generate guidelines for this repo at this time.',
      errorKind: 'failure',
    };
  }
};

export interface FetchTotalPRCountByReposRequest {
  org_uuid: string;
  repo_names: string[];
}

export interface FetchTotalPRCountByReposResponse {
  repo_counts: Record<string, number>;
  error?: string | null;
}

/**
 * Batch API call to fetch PR counts for multiple repositories in a single request
 * @param orgUUID - Organization UUID
 * @param repoNames - Array of repository names to fetch counts for
 * @returns Dictionary mapping repo_name -> count
 */
export const fetchTotalPRCountByRepos = async (
  orgUUID: string,
  repoNames: string[]
): Promise<FetchTotalPRCountByReposResponse> => {
  try {
    if (!orgUUID || repoNames.length === 0) {
      return { repo_counts: {}, error: null };
    }

    const payload: FetchTotalPRCountByReposRequest = {
      org_uuid: orgUUID,
      repo_names: repoNames,
    };

    const response = await postWithAuth(
      FETCH_TOTAL_PR_COUNT_BY_REPOS_URL,
      payload
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    // Handle both possible response formats
    if (data.error) {
      return { repo_counts: {}, error: data.error };
    }

    // Ensure all requested repos are in the response with at least 0 count
    const repoCounts: Record<string, number> = {};
    repoNames.forEach((repoName) => {
      repoCounts[repoName] = data.repo_counts?.[repoName] ?? 0;
    });

    return { repo_counts: repoCounts, error: null };
  } catch (error) {
    console.error('Error fetching total PR count by repos:', error);
    return {
      repo_counts: {},
      error: error instanceof Error ? error.message : String(error),
    };
  }
};

// ---------------------------------------------------------------------------
// Repo Markdown File Config
// ---------------------------------------------------------------------------

export interface RepoMarkdownFile {
  file_path: string;
  is_enabled: boolean;
}

export interface ScanRepoMarkdownFilesResult {
  files: RepoMarkdownFile[];
  warning?: string;
}

export const scanRepoMarkdownFiles = async (
  orgUuid: string,
  repoName: string
): Promise<ScanRepoMarkdownFilesResult> => {
  try {
    const response = await postWithAuth(SCAN_REPO_MARKDOWN_FILES_URL, {
      org_uuid: orgUuid,
      repo_name: repoName,
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data = await response.json();
    return { files: data.files ?? [], warning: data.warning };
  } catch (error) {
    console.error('scanRepoMarkdownFiles error:', error);
    throw error;
  }
};

export const fetchRepoMarkdownFiles = async (
  orgUuid: string,
  repoName: string
): Promise<RepoMarkdownFile[]> => {
  try {
    const response = await postWithAuth(FETCH_REPO_MARKDOWN_FILES_URL, {
      org_uuid: orgUuid,
      repo_name: repoName,
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data = await response.json();
    return data.files ?? [];
  } catch (error) {
    console.error('fetchRepoMarkdownFiles error:', error);
    throw error;
  }
};

export const updateRepoMarkdownFile = async (
  orgUuid: string,
  repoName: string,
  filePath: string,
  isEnabled: boolean
): Promise<void> => {
  try {
    const response = await postWithAuth(UPDATE_REPO_MARKDOWN_FILE_URL, {
      org_uuid: orgUuid,
      repo_name: repoName,
      file_path: filePath,
      is_enabled: isEnabled,
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
  } catch (error) {
    console.error('updateRepoMarkdownFile error:', error);
    throw error;
  }
};

export const fetchPRReviewExcludePatterns = async (
  orgUUID: string
): Promise<string[]> => {
  try {
    const response = await postWithAuth(FETCH_PR_REVIEW_EXCLUDE_PATTERNS_URL, {
      orgUUID,
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data = await response.json();
    return data.patterns ?? [];
  } catch (error) {
    console.error('fetchPRReviewExcludePatterns error:', error);
    return [];
  }
};

export const storePRReviewExcludePatterns = async (
  orgUUID: string,
  patterns: string[]
): Promise<void> => {
  try {
    const response = await postWithAuth(STORE_PR_REVIEW_EXCLUDE_PATTERNS_URL, {
      orgUUID,
      patterns,
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
  } catch (error) {
    console.error('storePRReviewExcludePatterns error:', error);
    throw error;
  }
};

export const bulkUpdateRepoMarkdownFiles = async (
  orgUuid: string,
  repoName: string,
  isEnabled: boolean
): Promise<void> => {
  try {
    const response = await postWithAuth(BULK_UPDATE_REPO_MARKDOWN_FILES_URL, {
      org_uuid: orgUuid,
      repo_name: repoName,
      is_enabled: isEnabled,
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
  } catch (error) {
    console.error('bulkUpdateRepoMarkdownFiles error:', error);
    throw error;
  }
};

export interface RepoMarkdownFolderConfig {
  folder_path: string;
  is_watched: boolean;
}

export const fetchRepoMarkdownFolderConfigs = async (
  orgUuid: string,
  repoName: string
): Promise<RepoMarkdownFolderConfig[]> => {
  try {
    const response = await postWithAuth(
      FETCH_REPO_MARKDOWN_FOLDER_CONFIGS_URL,
      {
        org_uuid: orgUuid,
        repo_name: repoName,
      }
    );
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data = await response.json();
    return data.folders ?? [];
  } catch (error) {
    console.error('fetchRepoMarkdownFolderConfigs error:', error);
    throw error;
  }
};

export const updateRepoMarkdownFolderConfig = async (
  orgUuid: string,
  repoName: string,
  folderPath: string,
  isWatched: boolean
): Promise<void> => {
  try {
    const response = await postWithAuth(
      UPDATE_REPO_MARKDOWN_FOLDER_CONFIG_URL,
      {
        org_uuid: orgUuid,
        repo_name: repoName,
        folder_path: folderPath,
        is_watched: isWatched,
      }
    );
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
  } catch (error) {
    console.error('updateRepoMarkdownFolderConfig error:', error);
    throw error;
  }
};
